import { useState, useEffect } from 'react';
import { Book } from '../../types';
import { useI18n } from '../../i18n';
import { FiBook, FiClock, FiStar, FiTrash2, FiExternalLink, FiRefreshCw } from 'react-icons/fi';

interface LibrarySidebarProps {
  books: Book[];
  currentFilter: string;
  onFilterChange: (filter: string) => void;
  onOpenSync?: () => void;
}

const LibrarySidebar = ({ books, currentFilter, onFilterChange, onOpenSync }: LibrarySidebarProps) => {
  const { t } = useI18n();

  // ── 로고 로테이션 (푸터와 동일 로직, 자체 WP API 사용) ──
  const LOGO_BASE = 'https://wiabook.com/assets/img/logos/';
  const [logoSrc, setLogoSrc] = useState(`${LOGO_BASE}1.png`);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    let ids: number[] = [];
    let cur = 0;

    fetch('https://wiabook.com/wp-json/wiabook-footer/v1/config')
      .then(r => r.json())
      .then(cfg => {
        if (!cfg.logo_enabled) return;
        const count: number = cfg.logo_count || 30;
        const interval: number = (cfg.logo_interval || 12) * 1000;
        // 1~count 중 25 제외
        ids = Array.from({ length: count }, (_, i) => i + 1).filter(n => n !== 25);
        ids = ids.sort(() => Math.random() - 0.5); // 셔플
        cur = 0;
        setLogoSrc(`${LOGO_BASE}${ids[cur]}.png`);
        timer = setInterval(() => {
          cur = (cur + 1) % ids.length;
          setLogoSrc(`${LOGO_BASE}${ids[cur]}.png`);
        }, interval);
      })
      .catch(() => { /* API 실패 시 기본 로고 유지 */ });

    return () => clearInterval(timer);
  }, []);
  // ──────────────────────────────────────────────────────────

  const totalBooks = books.length;
  const recentBooks = books.filter(b => b.status === 'reading' || b.lastRead).length;
  const favoriteBooks = books.filter(b => b.favorite).length;
  const deletedBooks = 0;

  const menuItems = [
    {
      id: 'all',
      labelKey: 'sidebar.allBooks',
      icon: FiBook,
      count: totalBooks,
      color: 'text-blue-600 dark:text-blue-400',
    },
    {
      id: 'recent',
      labelKey: 'sidebar.recentlyRead',
      icon: FiClock,
      count: recentBooks,
      color: 'text-green-600 dark:text-green-400',
    },
    {
      id: 'favorites',
      labelKey: 'sidebar.favorites',
      icon: FiStar,
      count: favoriteBooks,
      color: 'text-yellow-600 dark:text-yellow-400',
    },
    {
      id: 'deleted',
      labelKey: 'sidebar.deleted',
      icon: FiTrash2,
      count: deletedBooks,
      color: 'text-red-600 dark:text-red-400',
      disabled: true,
    },
  ];

  const promoLinks = [
    {
      titleKey: 'sidebar.aiReadingAssistant',
      descKey: 'sidebar.aiReadingAssistantDesc',
      href: 'https://wiabook.com/wia-epub-bookshelf/ai-assistant/',
      gradient: 'from-purple-500 to-pink-500',
    },
    {
      titleKey: 'sidebar.create3DPage',
      descKey: 'sidebar.create3DPageDesc',
      href: 'https://wiabook.com/3D_book-detail-page-maker.html',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      titleKey: 'sidebar.createBookCover',
      descKey: 'sidebar.createBookCoverDesc',
      href: 'https://wiabook.com/wiabooks-cover-calculator.html',
      gradient: 'from-orange-500 to-red-500',
    },
  ];

  return (
    <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Logo */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex flex-col items-center gap-1">
          <a href="/wia-epub-bookshelf/" aria-label="WIA Book 홈" title="WIA Book 홈">
            <img src={logoSrc} alt="WIA Book" className="h-12 w-auto cursor-pointer" style={{ transition: 'opacity 0.6s ease' }} />
          </a>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('app.tagline')}
          </p>
        </div>
      </div>

      {/* My Library */}
      <div className="p-4 flex-1">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
            {t('sidebar.myLibrary')}
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('sidebar.totalBooksShort', { count: totalBooks })}
          </p>
        </div>

        <nav className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentFilter === item.id;

            return (
              <button
                key={item.id}
                onClick={() => !item.disabled && onFilterChange(item.id)}
                disabled={item.disabled}
                className={`
                  w-full flex items-center justify-between px-3 py-2.5 rounded-lg
                  transition-all duration-200
                  ${isActive
                    ? 'bg-primary text-white shadow-md'
                    : item.disabled
                    ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <Icon size={20} className={isActive ? 'text-white' : item.color} />
                  <span className="font-medium">{t(item.labelKey)}</span>
                </div>
                <span className={`
                  text-xs font-medium px-2 py-0.5 rounded-full
                  ${isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }
                `}>
                  {item.count}
                </span>
              </button>
            );
          })}
        </nav>

        {/* Sync Button */}
        {onOpenSync && (
          <button
            onClick={onOpenSync}
            className="w-full mt-4 flex items-center gap-3 px-3 py-2.5 rounded-lg
              text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700
              border border-dashed border-gray-300 dark:border-gray-600
              transition-all duration-200"
          >
            <FiRefreshCw size={20} className="text-purple-600 dark:text-purple-400" />
            <span className="font-medium">{t('sync.button') || '기기간 동기화'}</span>
          </button>
        )}
      </div>

      {/* Promo Links */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
        {promoLinks.map((link, index) => (
          <a
            key={index}
            href={link.href}
            target={link.href.startsWith('http') ? '_blank' : undefined}
            rel={link.href.startsWith('http') ? 'noopener noreferrer' : undefined}
            className={`block p-3 rounded-xl bg-gradient-to-r ${link.gradient} text-white hover:shadow-lg transition-all hover:scale-[1.02]`}
          >
            <div className="flex items-center justify-between">
              <span className="font-bold text-sm">{t(link.titleKey)}</span>
              {link.href.startsWith('http') && <FiExternalLink size={14} />}
            </div>
            <p className="text-xs text-white/80 mt-1">{t(link.descKey)}</p>
          </a>
        ))}
      </div>
    </div>
  );
};

export default LibrarySidebar;
